import { parseCookies, setCookie } from 'nookies'
import FetchWrapper, { FetchData, FetchReturn } from './types'
import { NextApiRequest, NextPageContext } from 'next'
import * as next from 'next'

const setTokenAndUid = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    response: any,
    ctx:
        | Pick<next.NextPageContext, 'res'>
        | {
              res: next.NextApiResponse
          }
        | null
        | undefined
) => {
    if (response.token) {
        setCookie(ctx, 'customer_token', response.token, {
            maxAge: 365 * 24 * 60 * 60,
            path: '/'
        })
    }
    if (response.customerUid) {
        setCookie(ctx, 'customer_uid', response.customerUid, {
            path: '/'
        })
    }
}

const getTokenFromCookie = (
    ctx?:
        | Pick<NextPageContext, 'req'>
        | {
              req: NextApiRequest
          }
        | null
        | undefined
) => {
    const cookies = parseCookies(ctx)
    const token = cookies.customer_token ?? cookies.zdr_auth_token
    return token ? `Bearer ${token}` : ''
}

export type Method = 'GET' | 'POST' | 'PUT' | 'DELETE'

export enum ErrorType {
    NotError = 0,
    SqlError = 1,
    CodeError = 2,
    IsNotAuthorize = 3,
    IsNotValidate = 4,
    ShowToUser = 5,
    ShowToUserRedirect = 6
}

export type ErrorResponse = {
    IdError: string
    TypeDto: ErrorType
    Data: string
    IsError: boolean
}

const headerDefault = (
    ctx?:
        | Pick<NextPageContext, 'req'>
        | {
              req: NextApiRequest
          }
        | null
        | undefined
) =>
    new Headers({
        Accept: 'application/json',
        Authorization: getTokenFromCookie(ctx),
        'Content-Type': 'application/json'
    })

class FetchApi implements FetchWrapper {
    /**
     * @inheritDoc
     */
    public delete<T>(
        url: string,
        data: FetchData = null,
        ctx?:
            | Pick<NextPageContext, 'req'>
            | {
                  req: NextApiRequest
              }
            | null
            | undefined,
        headers: HeadersInit = headerDefault(ctx)
    ): Promise<FetchReturn<T | ErrorResponse>> {
        return FetchApi._fetch(url, 'DELETE', data, headers)
    }

    /**
     * @inheritDoc
     */
    public get<T>(
        url: string,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        data: FetchData = null,
        ctx?:
            | Pick<NextPageContext, 'req'>
            | {
                  req: NextApiRequest
              }
            | null
            | undefined,
        headers: HeadersInit = headerDefault(ctx)
    ): Promise<FetchReturn<T | ErrorResponse>> {
        return FetchApi._fetch(url, 'GET', null, headers)
    }

    /**
     * @inheritDoc
     */
    public post<T>(
        url: string,
        data: FetchData = null,
        ctx?:
            | Pick<NextPageContext, 'req'>
            | {
                  req: NextApiRequest
              }
            | null
            | undefined,
        headers: HeadersInit = headerDefault(ctx)
    ): Promise<FetchReturn<T | ErrorResponse>> {
        return FetchApi._fetch(url, 'POST', data, headers)
    }

    /**
     * @inheritDoc
     */
    public put<T>(
        url: string,
        data: FetchData = null,
        ctx?:
            | Pick<NextPageContext, 'req'>
            | {
                  req: NextApiRequest
              }
            | null
            | undefined,
        headers: HeadersInit = headerDefault(ctx)
    ): Promise<FetchReturn<T | ErrorResponse>> {
        return FetchApi._fetch(url, 'PUT', data, headers)
    }

    /**
     * Internal wrapper
     * @param url Url to fetch
     * @param methodName Method call
     * @param data Data to fetch
     * @param headers Headers for the request
     * @param ctx Request context Next.js
     * @private
     */
    private static async _fetch<T>(
        url: string,
        methodName: Method,
        data: FetchData,
        headers?: HeadersInit,
        ctx?:
            | Pick<next.NextPageContext, 'res'>
            | {
                  res: next.NextApiResponse
              }
            | null
            | undefined
    ): Promise<FetchReturn<T | ErrorResponse>> {
        let response
        if (data === null) {
            response = await fetch(url, {
                method: methodName,
                cache: 'no-cache',
                credentials: 'include',
                headers: headers || {
                    Accept: 'application/json',
                    'Content-Type': 'application/json'
                }
            })
        } else {
            response = await fetch(url, {
                method: methodName,
                cache: 'no-cache',
                credentials: 'include',
                headers: headers || {
                    Accept: 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(data)
            })
        }
        let responseData
        try {
            const contentType = response.headers.get('Content-Type')
            responseData =
                contentType && contentType.includes('application/json') ? await response.json() : await response.text()

            setTokenAndUid(responseData, ctx)
            if (!(response.status >= 200 && response.status < 300)) {
                throw new Error(`error response ${response.status} ${response.statusText} ${responseData}`)
            }
            return {
                data: responseData
            }
        } catch (err) {
            if (responseData != null) throw new Error(responseData)
            else throw new Error('unknown error response')
        }
    }
}

export const fetchApi = new FetchApi() as FetchWrapper
